import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Source } from '../../types';
import * as api from '../../services/api';

/**
 * Source combobox — "pick an existing source, or type a new name".
 *
 * Used on both Build New Data Body screens. It reports its value as a
 * discriminated pair rather than a single string, because the two cases hit
 * different fields on POST /api/data-bodies:
 *
 *   { sourceId }   — an existing source was picked from the list
 *   { sourceName } — a name that isn't in the list yet; the backend creates
 *                    it (case-insensitively de-duped) as part of the create
 *
 * Deliberately a plain input + filtered list rather than a <select>: a
 * <select> can't express "or type something new", and an autocomplete that
 * silently snaps to the nearest existing name is how you end up with leads
 * filed under the wrong partner.
 */

export type SourceSelection =
  | { kind: 'existing'; sourceId: string; name: string }
  | { kind: 'new'; name: string }
  | null;

interface Props {
  value: SourceSelection;
  onChange: (value: SourceSelection) => void;
  /** Rendered under the field. Use for create errors from the server. */
  error?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}

export default function SourceSelect({ value, onChange, error, autoFocus, disabled }: Props) {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [text, setText] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api.getSources()
      .then(list => { if (!cancelled) { setSources(list); setLoadError(null); } })
      .catch(err => {
        if (cancelled) {return;}
        // A failed list must NOT block creating a data body — the user can
        // still type a name and the backend will find-or-create it.
        setLoadError(err?.response?.data?.error || err?.message || 'Could not load sources');
      })
      .finally(() => { if (!cancelled) {setLoading(false);} });
    return () => { cancelled = true; };
  }, []);

  // Keep the text box in sync when the PARENT changes the field — e.g. the
  // Dashboard modal closing and reopening resets it to null.
  //
  // The trimmed comparison matters: every keystroke calls onChange, so `value`
  // is a new object each time. Assigning `value.name` unconditionally would
  // eat a trailing space the moment the user typed one ("Big " → "Big"), which
  // reads as a broken text box. Only overwrite when the name actually differs.
  useEffect(() => {
    if (!value) { setText(''); return; }
    setText(prev => (prev.trim() === value.name ? prev : value.name));
  }, [value]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {setOpen(false);}
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) {return sources;}
    return sources.filter(s => s.name.toLowerCase().includes(q));
  }, [sources, text]);

  // An exact (case-insensitive) hit means typing the name is the same thing
  // as picking it — so we must NOT offer "Create …" for it, or the user gets
  // a 409 from the unique index.
  const exact = useMemo(
    () => sources.find(s => s.name.toLowerCase() === text.trim().toLowerCase()) || null,
    [sources, text]
  );

  function commitText(raw: string) {
    const name = raw.trim();
    if (!name) {return onChange(null);}
    const hit = sources.find(s => s.name.toLowerCase() === name.toLowerCase());
    onChange(hit ? { kind: 'existing', sourceId: hit.id, name: hit.name } : { kind: 'new', name });
  }

  function pick(s: Source) {
    setText(s.name);
    onChange({ kind: 'existing', sourceId: s.id, name: s.name });
    setOpen(false);
  }

  const showCreateRow = text.trim().length > 0 && !exact;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="form-input"
        type="text"
        placeholder={loading ? 'Loading sources…' : 'e.g., BigSky Media'}
        value={text}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={e => { setText(e.target.value); setOpen(true); commitText(e.target.value); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Escape') {setOpen(false);}
          if (e.key === 'Enter' && open) {
            // Enter inside an open dropdown picks the single obvious match
            // instead of submitting the form behind it.
            if (matches.length === 1) { e.preventDefault(); pick(matches[0]); }
            else {setOpen(false);}
          }
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="source-select-listbox"
      />

      {open && !disabled && (matches.length > 0 || showCreateRow) && (
        <div
          id="source-select-listbox"
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            marginTop: 4,
            maxHeight: 240,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            boxShadow: '0 8px 20px rgba(0,0,0,0.10)',
          }}
        >
          {matches.map(s => (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={value?.kind === 'existing' && value.sourceId === s.id}
              onClick={() => pick(s)}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '9px 12px',
                border: 'none',
                borderBottom: '1px solid #f3f4f6',
                background: 'transparent',
                textAlign: 'left',
                fontSize: 14,
                color: '#111827',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontWeight: 500 }}>{s.name}</span>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                {s.bodyCount ? `${s.bodyCount} ${s.bodyCount === 1 ? 'body' : 'bodies'}` : 'no bodies yet'}
              </span>
            </button>
          ))}

          {showCreateRow && (
            <button
              type="button"
              onClick={() => { commitText(text); setOpen(false); }}
              style={{
                display: 'block',
                width: '100%',
                padding: '9px 12px',
                border: 'none',
                background: '#f9fafb',
                textAlign: 'left',
                fontSize: 13,
                color: '#1d4ed8',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + Create new source “{text.trim()}”
            </button>
          )}
        </div>
      )}

      {value?.kind === 'new' && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#92400e' }}>
          New source — “{value.name}” will be added to your Sources directory.
        </div>
      )}
      {loadError && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>
          {loadError}. You can still type a source name.
        </div>
      )}
      {error && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#dc2626' }}>{error}</div>
      )}
    </div>
  );
}
